import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { viabilidadeAPI, cepAPI, formatarErroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MapPin, Loader2, Search, CheckCircle2, AlertTriangle, Clock, SatelliteDish, History, XCircle, Hash, Building2, ArrowLeft, Cable, Users, CreditCard, FileBarChart2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ViabilidadeConector } from '@/components/ViabilidadeConector';
import { ConectorAviso } from '@/components/ConectorAviso';
import { CreditoConsulta } from '@/components/credito/CreditoConsulta';
import { RelatorioConsultas } from '@/components/relatorios/RelatorioConsultas';
import { PlanosClaro } from '@/components/viabilidade/PlanosClaro';

const fmtDataHora = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-');
const soDigitos = (v) => (v || '').replace(/\D/g, '');
const TIPO_EDIF = { C: 'Casa', A: 'Apartamento', P: 'Prédio', L: 'Loja', S: 'Sala' };
const edificacao = (h) => h.descricao_edificacao || TIPO_EDIF[h.tipo_edificacao] || h.tipo_edificacao || '';

const SimNao = ({ v }) => (v
  ? <span className="inline-flex items-center gap-1 font-semibold text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /> SIM</span>
  : <span className="inline-flex items-center gap-1 font-semibold text-red-500"><XCircle className="w-3.5 h-3.5" /> NÃO</span>);

const Aviso = ({ r }) => <ConectorAviso r={r} testid="via-aviso" />;

const ListaComplementos = ({ busca, onEscolher, carregandoHp }) => {
  const [filtro, setFiltro] = useState('');
  const [limite, setLimite] = useState(60);
  const filtrados = useMemo(() => {
    const f = filtro.trim().toUpperCase();
    return f ? busca.hps.filter((h) => `${h.complementos} ${h.hp}`.toUpperCase().includes(f)) : busca.hps;
  }, [busca.hps, filtro]);
  const logr = busca.logradouros?.[0];
  return (
    <div className="space-y-3" data-testid="via-complementos">
      <div className="rounded-lg border bg-gray-50 dark:bg-gray-800/50 p-3 text-sm">
        <p className="font-semibold flex items-center gap-2"><Cable className="w-4 h-4 text-red-600" /> {logr ? `${logr.tipo ? logr.tipo + ' ' : ''}${logr.logradouro}` : 'Logradouro'}, {busca.numero}</p>
        <p className="text-xs text-gray-500">{logr?.bairro} • {busca.cidade?.nome}/{busca.cidade?.uf} • COM CABO • <b>{busca.total}</b> complemento(s) encontrado(s)</p>
      </div>
      {busca.total === 0 ? (
        <p className="text-sm text-amber-700 dark:text-amber-300" data-testid="via-complementos-vazio">Nenhum HP com cabo encontrado para este número. Confira o número ou tente o CEP principal da rua.</p>
      ) : (
        <>
          <Input value={filtro} onChange={(e) => { setFiltro(e.target.value); setLimite(60); }} placeholder="Filtrar complemento (ex.: BL 2 APT 403)" data-testid="via-filtro-complemento" />
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b bg-gray-50 dark:bg-gray-800/60">
                  <th className="px-3 py-1.5 font-medium">Complemento</th>
                  <th className="px-3 py-1.5 font-medium">Nº</th>
                  <th className="px-3 py-1.5 font-medium hidden sm:table-cell">Tipo</th>
                  <th className="px-3 py-1.5 font-medium text-right">Serviços</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.slice(0, limite).map((h) => (
                  <tr key={h.hp} className="border-b last:border-0 hover:bg-purple-50/60 dark:hover:bg-purple-900/10" data-testid={`via-hp-${h.hp}`}>
                    <td className="px-3 py-1.5 font-medium">{h.complementos || <span className="text-gray-400 italic">sem complemento</span>}</td>
                    <td className="px-3 py-1.5">{h.numero}</td>
                    <td className="px-3 py-1.5 hidden sm:table-cell text-gray-500">{edificacao(h)}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Button size="sm" variant="outline" className="h-7" disabled={carregandoHp === h.hp} onClick={() => onEscolher(h)} data-testid={`via-ver-servicos-${h.hp}`}>
                        {carregandoHp === h.hp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Ver serviços'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtrados.length > limite && (
            <Button variant="ghost" size="sm" onClick={() => setLimite((l) => l + 100)} className="w-full" data-testid="via-mostrar-mais">Mostrar mais ({filtrados.length - limite} restantes)</Button>
          )}
        </>
      )}
    </div>
  );
};

const DetalheServicos = ({ d, onVoltar }) => (
  <div className="space-y-3" data-testid="via-detalhe">
    <Button variant="ghost" size="sm" onClick={onVoltar} className="-ml-2" data-testid="via-voltar-complementos"><ArrowLeft className="w-4 h-4 mr-1" /> Voltar aos complementos</Button>
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex items-center gap-2 rounded-lg bg-purple-600 text-white px-3 py-1.5 text-sm font-semibold" data-testid="via-hp"><Hash className="w-4 h-4" /> HP {d.hp}</div>
      {edificacao(d) && <Badge variant="outline" className="gap-1"><Building2 className="w-3 h-3" /> {edificacao(d)}</Badge>}
      {d.vendaveis?.length > 0
        ? <Badge className="bg-emerald-600 hover:bg-emerald-600 gap-1"><CheckCircle2 className="w-3 h-3" /> {d.vendaveis.length} produto(s) vendável(is)</Badge>
        : <Badge className="bg-red-600 hover:bg-red-600 gap-1"><XCircle className="w-3 h-3" /> Sem produto vendável</Badge>}
    </div>
    <div className="rounded-lg border bg-gray-50 dark:bg-gray-800/50 p-3 text-sm">
      <p className="font-semibold">{d.logradouro}, {d.numero} {d.complementos ? `— ${d.complementos}` : ''}</p>
      <p className="text-xs text-gray-500">{d.bairro} • CEP {d.cep}</p>
    </div>
    <div className="overflow-hidden rounded-lg border bg-white dark:bg-gray-900">
      <div className="px-3 py-2 border-b bg-gray-50 dark:bg-gray-800/60 text-sm font-semibold">Serviços disponíveis</div>
      <table className="w-full text-sm" data-testid="via-servicos-tabela">
        <thead>
          <tr className="text-left text-gray-500 border-b">
            <th className="px-3 py-1.5 font-medium">Produto</th>
            <th className="px-3 py-1.5 font-medium">Técnica</th>
            <th className="px-3 py-1.5 font-medium">Comercial</th>
          </tr>
        </thead>
        <tbody>
          {d.servicos.map((s) => (
            <tr key={s.tipo} className="border-b last:border-0" data-testid={`via-servico-${s.tipo}`}>
              <td className="px-3 py-1.5 font-medium">{s.tipo}</td>
              <td className="px-3 py-1.5"><SimNao v={s.tecnica} /></td>
              <td className="px-3 py-1.5"><SimNao v={s.comercial} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {d.codificacao && (
        <div className="px-3 py-2 border-t text-xs"><span className="text-gray-500">Codificação: </span><span className="font-medium">{d.codificacao}</span></div>
      )}
    </div>
    {d.propostas?.length > 0 && (
      <div className="rounded-lg border p-3 text-xs" data-testid="via-propostas">
        <p className="font-semibold flex items-center gap-1.5 mb-1.5"><Users className="w-3.5 h-3.5" /> Contratos neste endereço ({d.propostas.length})</p>
        <div className="space-y-1">
          {d.propostas.slice(0, 8).map((p, i) => (
            <div key={i} className="flex items-center justify-between gap-2">
              <span className="truncate">{p.nome || 'Cliente'} <span className="text-gray-400">• {p.contrato}</span></span>
              <Badge variant="outline" className={`shrink-0 text-[10px] ${/ATIV|CONECT/i.test(p.situacao) && !/DESCONECT/i.test(p.situacao) ? 'text-emerald-600' : 'text-gray-500'}`}>{p.situacao}</Badge>
            </div>
          ))}
          {d.propostas.length > 8 && <p className="text-gray-400">+{d.propostas.length - 8} outros</p>}
        </div>
      </div>
    )}
    <p className="text-[11px] text-gray-500">Consultado em {fmtDataHora(d.consultado_em)} • via conector do navegador</p>
  </div>
);

const Viabilidade = () => {
  const { user } = useAuth();
  const isAdmin = user?.perfil === 'ADMIN';

  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ cep: '', numero: '', logradouro: '', bairro: '', cidade: '', uf: '' });
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [busca, setBusca] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [carregandoHp, setCarregandoHp] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [aba, setAba] = useState(() => { const a = new URLSearchParams(window.location.search).get('aba'); return ['credito', 'relatorio'].includes(a) ? a : 'viabilidade'; });
  const [creditoAtual, setCreditoAtual] = useState(null);

  const carregarConfig = useCallback(async () => {
    if (!isAdmin) return;
    try { setConfig((await viabilidadeAPI.config()).data); } catch (e) { /* silencioso */ }
  }, [isAdmin]);
  const carregarHistorico = useCallback(async () => {
    try { setHistorico((await viabilidadeAPI.historico()).data?.consultas || []); } catch (e) { /* silencioso */ }
  }, []);

  useEffect(() => { carregarConfig(); carregarHistorico(); }, [carregarConfig, carregarHistorico]);
  useEffect(() => {
    if (!isAdmin) return;
    const t = setInterval(carregarConfig, 20000);
    return () => clearInterval(t);
  }, [isAdmin, carregarConfig]);

  const buscarCep = async (cepRaw) => {
    const cep = soDigitos(cepRaw);
    if (cep.length !== 8) return;
    setBuscandoCep(true);
    try {
      const d = (await cepAPI.buscar(cep)).data || {};
      setForm((f) => ({ ...f, logradouro: d.logradouro || '', bairro: d.bairro || '', cidade: d.localidade || '', uf: d.uf || '' }));
    } catch (e) {
      toast.error('CEP não encontrado');
    } finally {
      setBuscandoCep(false);
    }
  };

  const buscarComplementos = async () => {
    if (soDigitos(form.cep).length !== 8) { toast.error('Informe um CEP válido (8 dígitos)'); return; }
    if (!soDigitos(form.numero)) { toast.error('Informe o número'); return; }
    if (!form.cidade || !form.uf) { toast.error('Cidade/UF não identificados — confira o CEP'); return; }
    setBuscando(true); setBusca(null); setDetalhe(null); setAviso(null);
    try {
      const res = (await viabilidadeAPI.complementos({ cep: soDigitos(form.cep), numero: form.numero, cidade: form.cidade, uf: form.uf })).data;
      if (res.status === 'ok') {
        setBusca(res);
      } else {
        setAviso(res);
      }
      carregarHistorico();
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setBuscando(false);
    }
  };

  const escolherHp = async (h, b = busca) => {
    setCarregandoHp(h.hp); setAviso(null);
    try {
      const res = (await viabilidadeAPI.detalhe({ hp: h.hp, cidade_codigo: b?.cidade?.codigo, cidade: b?.cidade?.nome, uf: b?.cidade?.uf })).data;
      if (res.status === 'ok') setDetalhe(res); else setAviso(res);
      carregarHistorico();
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setCarregandoHp(null);
    }
  };

  const StatusBadge = () => {
    if (!config) return null;
    const c = config.conector;
    if (c?.ativo && c.sessao !== 'caiu') return <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-600" data-testid="via-status-badge"><CheckCircle2 className="w-3 h-3" /> Pronto</Badge>;
    if (!c?.pareado) return <Badge variant="outline" className="gap-1 text-gray-500" data-testid="via-status-badge"><AlertTriangle className="w-3 h-3" /> Conector não instalado</Badge>;
    return <Badge className="gap-1 bg-amber-500 hover:bg-amber-500" data-testid="via-status-badge"><Clock className="w-3 h-3" /> Aguardando conector</Badge>;
  };

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  return (
    <div className="space-y-6 max-w-5xl" data-testid="viabilidade-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><MapPin className="w-6 h-6 text-red-600" /> Viabilidade Claro</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {aba === 'credito'
              ? 'Checagem de crédito • informe CPF/CNPJ, nome, nascimento e CEP → veja se o cliente está aprovado na Claro.'
              : aba === 'relatorio'
                ? 'Relatório • endereços consultados e checagens de crédito por período e colaborador, com exportação Excel.'
                : 'COM CABO • digite CEP e número → veja todos os complementos → escolha um e veja os produtos que podem ser vendidos.'}
          </p>
        </div>
        <StatusBadge />
      </div>

      {isAdmin && <ViabilidadeConector config={config} onAtualizar={carregarConfig} />}

      <Tabs value={aba} onValueChange={setAba}>
        <TabsList className="grid w-full max-w-lg grid-cols-3" data-testid="via-tabs">
          <TabsTrigger value="viabilidade" className="gap-1.5" data-testid="via-tab-viabilidade"><MapPin className="w-4 h-4" /> Viabilidade</TabsTrigger>
          <TabsTrigger value="credito" className="gap-1.5" data-testid="via-tab-credito"><CreditCard className="w-4 h-4" /> Crédito</TabsTrigger>
          <TabsTrigger value="relatorio" className="gap-1.5" data-testid="via-tab-relatorio"><FileBarChart2 className="w-4 h-4" /> Relatório</TabsTrigger>
        </TabsList>
        <TabsContent value="relatorio" className="mt-4">
          {aba === 'relatorio' && <RelatorioConsultas />}
        </TabsContent>
        <TabsContent value="credito" className="mt-4">
          <CreditoConsulta onResultado={setCreditoAtual} />
        </TabsContent>
        <TabsContent value="viabilidade" className="mt-4">
      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><SatelliteDish className="w-4 h-4" /> Consultar viabilidade</CardTitle>
            <CardDescription>Digite o CEP e o número. O endereço é preenchido automaticamente.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">CEP</Label>
                <div className="relative mt-1">
                  <Input value={form.cep} onChange={set('cep')} onBlur={(e) => buscarCep(e.target.value)} placeholder="00000-000" inputMode="numeric" data-testid="via-cep-input" />
                  {buscandoCep && <Loader2 className="w-4 h-4 animate-spin absolute right-2 top-2.5 text-gray-400" />}
                </div>
              </div>
              <div>
                <Label className="text-xs">Número</Label>
                <Input value={form.numero} onChange={set('numero')} placeholder="123" inputMode="numeric" className="mt-1" data-testid="via-numero-input" onKeyDown={(e) => e.key === 'Enter' && buscarComplementos()} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-3 sm:col-span-1"><Label className="text-xs">Logradouro</Label><Input value={form.logradouro} onChange={set('logradouro')} className="mt-1" data-testid="via-logradouro-input" /></div>
              <div><Label className="text-xs">Cidade</Label><Input value={form.cidade} onChange={set('cidade')} className="mt-1" data-testid="via-cidade-input" /></div>
              <div><Label className="text-xs">UF</Label><Input value={form.uf} onChange={set('uf')} maxLength={2} className="mt-1 uppercase" data-testid="via-uf-input" /></div>
            </div>
            <Button onClick={buscarComplementos} disabled={buscando} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="via-buscar-button">
              {buscando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} {buscando ? 'Consultando a Claro…' : 'Buscar complementos'}
            </Button>
            <Aviso r={aviso} />
            {detalhe
              ? <DetalheServicos d={detalhe} onVoltar={() => setDetalhe(null)} />
              : busca && <ListaComplementos busca={busca} onEscolher={escolherHp} carregandoHp={carregandoHp} />}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Últimas consultas</CardTitle>
            <CardDescription>{isAdmin ? 'Todas as consultas recentes.' : 'Suas consultas recentes.'}</CardDescription>
          </CardHeader>
          <CardContent>
            {historico.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="via-historico-vazio">Nenhuma consulta ainda.</p>
            ) : (
              <div className="divide-y rounded-lg border" data-testid="via-historico">
                {historico.map((h) => (
                  <div key={h.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{h.logradouro}, {h.numero}{h.complementos ? ` — ${h.complementos}` : ''}</p>
                      <p className="text-[11px] text-gray-500 truncate">HP {h.hp} • {h.bairro} • {fmtDataHora(h.respondida_em || h.criado_em)}</p>
                    </div>
                    {h.vendaveis?.length > 0
                      ? <Badge className="bg-emerald-600 hover:bg-emerald-600 shrink-0">{h.vendaveis.length} vendável(is)</Badge>
                      : <Badge variant="outline" className="text-red-500 shrink-0">Sem produto</Badge>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {detalhe?.status === 'ok' && (
        <div className="mt-6">
          <PlanosClaro uf={busca?.cidade?.uf || detalhe.uf} cidade={busca?.cidade?.nome || detalhe.cidade} servicos={detalhe.servicos} credito={creditoAtual} escolherCredito testid="via-planos" />
        </div>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Viabilidade;
